import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "lockNotice"]
  static values = {
    identifiersSelector: String
  }

  connect() {
    // Store initial value before any evaluation
    this.initialValue = this.checkboxTarget.checked
    
    this.observeIdentifiers()
    this.evaluate()
    
    // Setup observer for checkbox attributes (like disabled)
    this.setupCheckboxObserver()
    
    // Update visual state initially
    this.updateVisualState()
    
    // Listen for changes to update visual state
    this.checkboxTarget.addEventListener('change', () => this.updateVisualState())
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.checkboxObserver) this.checkboxObserver.disconnect()
  }

  observeIdentifiers() {
    const container = this.identifiersContainer()
    if (!container) return
    this.observer = new MutationObserver(() => this.evaluate())
    this.observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] })
    container.addEventListener("change", (e) => {
      if (e.target && e.target.name && (e.target.name.includes("[id_type]") || e.target.name.includes("[_destroy]"))) {
        this.evaluate()
      }
    })
  }

  identifiersContainer() {
    return document.querySelector(this.identifiersSelectorValue)
  }

  scopusOrWosPresent() {
    const container = this.identifiersContainer()
    if (!container) return false
    
    const rows = container.querySelectorAll(".nested-fields")
    
    for (const row of rows) {
      // Skip hidden rows (display: none)
      if (row.style.display === "none") continue
      
      // Skip rows marked for destruction
      const destroyField = row.querySelector("input[name*='[_destroy]']")
      if (destroyField && destroyField.value === "1") continue
      
      // Check the id_type select
      const select = row.querySelector("select[name*='[id_type]']")
      if (!select) continue
      
      const value = select.value
      // Check both string keys and numeric enum values
      if (["scopus_id", "wos_id", "2", "3"].includes(value)) {
        return true
      }
    }
    
    return false
  }

  evaluate() {
    const hasIndexedId = this.scopusOrWosPresent()
    
    if (hasIndexedId) {
      // Force true and lock when Scopus/WoS identifiers exist
      this.checkboxTarget.checked = true
      this.checkboxTarget.disabled = true
      if (this.hasLockNoticeTarget) {
        this.lockNoticeTarget.classList.remove("d-none")
      }
    } else {
      // No Scopus/WoS identifiers: unlock but preserve current value
      // Only set to false on first evaluation if it was initially false
      if (this.initialValue !== undefined) {
        this.checkboxTarget.checked = this.initialValue
        this.initialValue = undefined // Clear after first use
      }
      // Otherwise keep whatever value the user has set
      
      this.checkboxTarget.disabled = false
      if (this.hasLockNoticeTarget) {
        this.lockNoticeTarget.classList.add("d-none")
      }
    }
    
    // Update visual state after evaluation
    this.updateVisualState()
  }
  
  toggleCheckbox(event) {
    event.preventDefault()
    if (!this.checkboxTarget.disabled) {
      this.checkboxTarget.checked = !this.checkboxTarget.checked
      this.checkboxTarget.dispatchEvent(new Event('change', { bubbles: true }))
      
      // Update visual state after toggle
      this.updateVisualState()
    }
  }
  
  // Setup observer for checkbox attribute changes
  setupCheckboxObserver() {
    this.checkboxObserver = new MutationObserver(() => this.updateVisualState())
    this.checkboxObserver.observe(this.checkboxTarget, { attributes: true })
  }
  
  // Update the visual appearance of the toggle
  updateVisualState() {
    const slider = this.element.querySelector('.toggle-slider')
    if (!slider) return
    
    // Apply styles based on checkbox state
    if (this.checkboxTarget.checked) {
      slider.classList.add('is-checked')
    } else {
      slider.classList.remove('is-checked')
    }
    
    // Apply locked styling if disabled
    if (this.checkboxTarget.disabled) {
      slider.classList.add('is-locked')
    } else {
      slider.classList.remove('is-locked')
    }
  }
}